import { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import FileUpload from "../../shared/FileUpload/index.tsx";
import MultiImageUpload from "../../shared/MultiImageUpload/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import "../../styles/scss/Form.scss";
import { imagepathReturn } from "../../config/formatters.ts";

export default function GalleryCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [editImagePreview, setEditImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const isEdit = id && id !== "new";

  const formik = useFormik({
    initialValues: {
      title: "",
      label: "",
      image: null,
      date: "",
      sort_order: "",
      isActive: "Active",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required"),
      sort_order: Yup.string().required("Sort Order is required"),
      image: isEdit ? Yup.mixed().nullable() : Yup.mixed().required("Image is required"),
    }),
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("label", values.label || "");
      formData.append("date", values.date || "");
      formData.append("sort_order", values.sort_order);
      formData.append("status", values?.isActive === "Active" ? "true" : "false");
      if (values.image) {
        formData.append("image", values.image);
      }

      try {
        let response;
        if (isEdit) {
          formData.append("_method", "PUT");
          response = await request.post("galleries/" + id, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          response = await request.post("galleries/create", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }

        if (response?.data?.status) {
          toast.success(isEdit ? "Gallery updated successfully!" : "Gallery created successfully!");
          navigate(`/admin/gallery/${id || response.data.data?.id}`);
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "An error occurred. Please try again.");
      }
    },
  });

  const [galleryFiles, setGalleryFiles] = useState<any[]>([]);

  const fetchGalleryFiles = async (galleryId: string) => {
    try {
      const response = await request.get(`galleries/${galleryId}/files`);
      setGalleryFiles(
        (response.data.data || []).map((f: any) => ({
          id: f.id,
          fileUrl: imagepathReturn(f.file_path),
        })),
      );
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (isEdit) {
      const fetchMember = async () => {
        setLoading(true);
        try {
          const response = await request.get(`galleries/${id}`);
          const item = response.data.data;
          formik.setValues({
            title: item.title || "",
            label: item.label || "",
            image: null,
            date: item.date || "",
            sort_order: item.sort_order?.toString() || "",
            isActive: item.status ? "Active" : "Inactive",
          });
          setEditImagePreview(imagepathReturn(item.image || null));
          await fetchGalleryFiles(id as string);
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      };

      fetchMember();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={isEdit ? "Edit Gallery" : "Create New Gallery"}
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/gallery"),
              }}
              primaryButton={{
                label: isEdit ? "Update Gallery" : "Create Gallery",
                icon: <span className="material-symbols-outlined">save</span>,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <TextField name="title" label="Title" type="text" formik={formik} autoFocus />
                </Box>
                <Box className="form-field">
                  <TextField name="label" label="Label" type="text" formik={formik} />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField name="date" label="Date" type="date" formik={formik} />
                </Box>
                <Box className="form-field">
                  <TextField name="sort_order" label="Sort Order" type="number" formik={formik} />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <SelectField
                    name="isActive"
                    label="Status"
                    formik={formik}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                  />
                </Box>
                <Box className="form-field">
                  <FileUpload
                    name="image"
                    formik={formik}
                    label="Upload Cover Image"
                    editImagePreview={editImagePreview}
                  />
                </Box>
              </Box>

              {isEdit && (
                <Box sx={{ marginTop: "24px" }}>
                  <Typography sx={{ fontWeight: 600, color: "#1a1f3a", mb: 1.5 }}>
                    Gallery Images
                  </Typography>
                  <MultiImageUpload
                    apiUrl={`galleries/${id}/files`}
                    payload={{ gallery_id: id }}
                    existingImages={galleryFiles}
                    deleteApiUrl="gallery-files"
                    deleteApiRefetch={() => fetchGalleryFiles(id as string)}
                    onUploadSuccess={() => fetchGalleryFiles(id as string)}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}